import { memo, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import ReactMarkdown, { defaultUrlTransform, type Components } from "react-markdown";
import type { PluggableList } from "unified";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { Icon } from "@/components/ui/Icon";
import { ImageLightbox } from "@/components/ui/ImageLightbox";
import { FileViewer } from "@/components/ui/FileViewer";
import { BRAND } from "@/lib/tokens";
import { rehypeKuerzel, type KuerzelDir } from "@/lib/kuerzel";

// Sanitize schema: GitHub's safe default (allows tables + basic formatting,
// strips scripts / event handlers / styles), extended to keep our internal
// `mention:` link scheme so @-mention chips survive. rehype-raw parses the HTML
// that tiptap-markdown emits for complex tables; rehype-sanitize then makes it
// safe, so pasted <table> content renders but a pasted <script> can't.
const sanitizeSchema = {
  ...defaultSchema,
  // GitHub's defaultSchema does NOT allow <mark>; without this the orange
  // highlight looks right in the editor and vanishes on render. No attributes are
  // permitted on it, so a stored body cannot smuggle an inline style.
  tagNames: [...(defaultSchema.tagNames ?? []), "mark"],
  protocols: {
    ...defaultSchema.protocols,
    href: [...(defaultSchema.protocols?.href ?? []), "mention"],
  },
};

// Full Markdown renderer for comment bodies (and other rich text). react-markdown
// builds React elements — no dangerouslySetInnerHTML, so no XSS surface — and we
// style every node to the CVI here. Supports what the TipTap composer emits:
// bold/italic, inline code + fenced code blocks, bullet/ordered lists, headings,
// blockquotes, links, and inline images (the comment attachment flow).
//
// 🔴 STABLE IDENTITY MATTERS (comment-media flicker, fixed 2026-08-03). react-markdown
// renders each node through the mapped component, so React reconciles a node by the
// IDENTITY of `components.img` / `components.a`. If these — or the plugin arrays —
// are recreated on every render, react-markdown hands React a new element TYPE at the
// media position and React unmounts + remounts the DOM node. A remounted <img>
// re-decodes and a remounted <video> reloads (poster flash + playback reset) — the
// flicker colleagues saw when hovering/scrolling comments (each hover toggles
// CommentItem's `hover` state → parent re-render). So: static config lives at module
// scope, `components` is a `useMemo([])` (its only closures are the stable useState
// setters), and the component is `memo`-wrapped so an unchanged body skips reparse.

// Static react-markdown config — module scope so its identity never changes across
// renders (see the flicker note above). remark-breaks turns a single newline into a
// real <br>; the mention: scheme is preserved so @-chips survive URL sanitization.
const remarkPlugins = [remarkGfm, remarkBreaks];
const urlTransform = (url: string) => (url.startsWith("mention:") ? url : defaultUrlTransform(url));

const inlineCode: CSSProperties = {
  fontFamily: "JetBrains Mono Variable, ui-monospace, monospace",
  fontSize: "0.9em",
  background: "var(--surface-soft)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "0.5px 4px",
  whiteSpace: "break-spaces",
};

const blockCode: CSSProperties = {
  margin: "8px 0",
  padding: "10px 12px",
  background: "var(--surface-soft)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  overflowX: "auto",
  fontFamily: "JetBrains Mono Variable, ui-monospace, monospace",
  fontSize: 12.5,
  lineHeight: 1.5,
  color: "var(--ink-1)",
};

const fileChip: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  margin: "4px 0",
  padding: "5px 10px",
  borderRadius: 7,
  border: "1px solid var(--hairline)",
  background: "var(--surface)",
  color: "var(--accent)",
  fontSize: 12.5,
  fontWeight: 500,
  textDecoration: "none",
  maxWidth: "100%",
};

function MarkdownContentInner({
  markdown,
  kuerzel,
  variant,
}: {
  markdown: string;
  // When set (memo bodies), highlight employee Kürzel in each person's identity
  // hue. Runs AFTER sanitize so the trusted spans it adds survive.
  kuerzel?: KuerzelDir;
  // "memo": render lists via the `flow-md-list` CLASS instead of an inline
  // paddingLeft, so context-scoped CSS can align a TOP-LEVEL list to the prose-
  // point column (a single "- point" reads identically to a prose point) while
  // indenting only genuinely-nested lists. Inline padding can't tell top-level
  // from nested; a CSS descendant selector can. Other consumers (comments, board
  // descriptions, long-text cells) omit `variant` and keep the default padding.
  variant?: "memo";
}) {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [fileView, setFileView] = useState<{ url: string; name: string } | null>(null);

  // rehype-raw parses embedded HTML (complex tables from tiptap-markdown);
  // rehype-sanitize then strips anything unsafe — so tables render but no XSS
  // surface is opened. Order matters: raw BEFORE sanitize. Memoized so its
  // identity only changes when `kuerzel` does (see the flicker note above).
  const rehypePlugins = useMemo<PluggableList>(
    () =>
      kuerzel
        ? [rehypeRaw, [rehypeSanitize, sanitizeSchema], rehypeKuerzel(kuerzel)]
        : [rehypeRaw, [rehypeSanitize, sanitizeSchema]],
    [kuerzel],
  );

  // Stable component map — the useState setters it closes over are identity-stable,
  // so `[]` deps are correct and the media nodes reconcile in place across renders.
  // Depends on `variant` (read in ul/ol). Stays identity-stable in practice — a
  // consumer passes a constant variant — so the flicker fix (media nodes reconcile
  // in place) holds; only a variant CHANGE (which never happens mid-mount) rebuilds it.
  const components = useMemo<Components>(
    () => ({
      p: ({ children }) => <p style={{ margin: "4px 0", lineHeight: 1.5 }}>{children}</p>,
      // Fixed colours in both themes — see .flow-md mark in index.css.
      mark: ({ children }) => <mark>{children}</mark>,
      h1: ({ children }) => <Heading level={1}>{children}</Heading>,
      h2: ({ children }) => <Heading level={2}>{children}</Heading>,
      h3: ({ children }) => <Heading level={3}>{children}</Heading>,
      h4: ({ children }) => <Heading level={3}>{children}</Heading>,
      h5: ({ children }) => <Heading level={3}>{children}</Heading>,
      h6: ({ children }) => <Heading level={3}>{children}</Heading>,
      strong: ({ children }) => <strong style={{ fontWeight: 700 }}>{children}</strong>,
      em: ({ children }) => <em>{children}</em>,
      ul: ({ children }) =>
        variant === "memo" ? (
          // Positional margin/padding live in CSS (.flow-md-list) so top-level vs
          // nested can differ; only the marker glyph stays inline.
          <ul className="flow-md-list" style={{ listStyleType: "disc" }}>
            {children}
          </ul>
        ) : (
          <ul style={{ margin: "4px 0", paddingLeft: 22, listStyleType: "disc" }}>{children}</ul>
        ),
      // `start` carries the number a list begins at ("4. x" -> 4; a list starting at 1 gets
      // none). Dropped, every list in the saved view, print and "Nur Neues" counted from 1.
      ol: ({ children, start }) =>
        variant === "memo" ? (
          <ol className="flow-md-list" start={start} style={{ listStyleType: "decimal" }}>
            {children}
          </ol>
        ) : (
          <ol start={start} style={{ margin: "4px 0", paddingLeft: 22, listStyleType: "decimal" }}>
            {children}
          </ol>
        ),
      li: ({ children }) => <li style={{ margin: "2px 0", lineHeight: 1.5 }}>{children}</li>,
      table: ({ children }) => (
        <div style={{ overflowX: "auto", margin: "8px 0" }}>
          <table style={{ borderCollapse: "collapse", fontSize: 13 }}>{children}</table>
        </div>
      ),
      th: ({ children }) => (
        <th
          style={{
            border: "1px solid var(--hairline-strong)",
            padding: "4px 8px",
            textAlign: "left",
            background: "var(--surface-soft)",
            fontWeight: 600,
          }}
        >
          {children}
        </th>
      ),
      td: ({ children }) => (
        <td
          style={{
            border: "1px solid var(--hairline-strong)",
            padding: "4px 8px",
            verticalAlign: "top",
          }}
        >
          {children}
        </td>
      ),
      blockquote: ({ children }) => (
        <blockquote
          style={{
            margin: "6px 0",
            padding: "2px 0 2px 12px",
            borderLeft: `3px solid ${BRAND.blue}`,
            color: "var(--ink-2)",
          }}
        >
          {children}
        </blockquote>
      ),
      // pre passes through; the code renderer decides inline vs block so a
      // fenced block (even without a language) is styled correctly.
      pre: ({ children }) => <>{children}</>,
      code: ({ className, children }) => {
        const text = String(children ?? "").replace(/\n$/, "");
        const isBlock = (className?.includes("language-") ?? false) || text.includes("\n");
        return isBlock ? (
          <pre style={blockCode}>
            <code>{text}</code>
          </pre>
        ) : (
          <code style={inlineCode}>{children}</code>
        );
      },
      a: ({ href, children }) => {
        const url = href ?? "";
        if (url.startsWith("mention:")) {
          return (
            <span
              style={{
                // Must match the editor's `.flow-rte-content .flow-mention`
                // — a mention looked right while typing and reverted to the
                // unreadable static brand blue once the comment was posted.
                color: "var(--accent)",
                background: "var(--accent-soft)",
                borderRadius: 5,
                padding: "0 4px",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              @{children}
            </span>
          );
        }
        if (url.startsWith("/api/files/")) {
          // Video attachments embed as [name](url) (not images); the file
          // URL is a UUID with no extension, so detect from the link name.
          if (/\.(mp4|mov|webm|m4v)$/i.test(childText(children))) {
            return (
              <video
                src={url}
                controls
                style={{
                  display: "block",
                  margin: "6px 0",
                  maxWidth: "100%",
                  borderRadius: 6,
                  border: "1px solid var(--border)",
                }}
              />
            );
          }
          // PDFs and .eml/.msg mails open IN Flow (the FileViewer modal)
          // instead of downloading.
          // 🔴 Route on the URL PATH extension, NOT the link label: a stored
          // file URL is /api/files/<uuid>/<name>.<realext> (extension preserved
          // + allowlist-validated), so a .pdf path is genuinely served as
          // application/pdf. Keying on the label would let
          // [report.pdf](/api/files/x/evil.svg) render a same-origin SVG
          // document in the iframe → stored XSS.
          const filePath = url.split(/[?#]/)[0] ?? "";
          const isPdf = /\.pdf$/i.test(filePath);
          const isMail = /\.(eml|msg)$/i.test(filePath);
          if (isPdf || isMail) {
            const name = childText(children);
            return (
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                // Mail on mobile has no in-app viewer → download instead of a
                // useless new-tab of the raw file.
                {...(isMail ? { download: true } : {})}
                style={{ ...fileChip, cursor: "pointer" }}
                onClick={(e) => {
                  // Never bubble to a click-to-edit parent (the ticket
                  // Beschreibung field swaps in an editor otherwise).
                  e.stopPropagation();
                  // Mobile: iframe rendering is unreliable → let the default
                  // link behave (PDF → browser view in a new tab; mail →
                  // download via the attribute above).
                  if (window.matchMedia("(max-width: 640px)").matches) return;
                  e.preventDefault();
                  setFileView({ url, name });
                }}
              >
                <Icon name="file" size={14} color="var(--accent)" />
                <span style={{ wordBreak: "break-all" }}>{children}</span>
              </a>
            );
          }
          return (
            <a href={url} target="_blank" rel="noreferrer" download style={fileChip}>
              <Icon name="file" size={14} color="var(--accent)" />
              <span style={{ wordBreak: "break-all" }}>{children}</span>
            </a>
          );
        }
        return (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            style={{ color: "var(--accent)", wordBreak: "break-all" }}
          >
            {children}
          </a>
        );
      },
      img: ({ src, alt }) => {
        const s = typeof src === "string" ? src : undefined;
        // Click opens the in-app lightbox (was: new browser tab).
        return (
          <img
            src={s}
            alt={alt ?? ""}
            onClick={(e) => {
              // Don't bubble to a click-to-edit parent (e.g. the ticket
              // Beschreibung detail field) — that would swap in the editor
              // and clobber the lightbox. Just open the viewer.
              e.stopPropagation();
              if (s) setLightboxSrc(s);
            }}
            style={{
              display: "block",
              margin: "6px 0",
              maxWidth: "100%",
              borderRadius: 6,
              border: "1px solid var(--border)",
              cursor: s ? "zoom-in" : "default",
            }}
          />
        );
      },
    }),
    [variant],
  );

  return (
    <div className="flow-md">
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        // Preserve our internal mention: scheme (rendered as a chip by the `a`
        // component); everything else keeps react-markdown's default URL
        // sanitization. Both this and `components` are stable identities (see note).
        urlTransform={urlTransform}
        components={components}
      >
        {markdown}
      </ReactMarkdown>
      {lightboxSrc ? (
        <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      ) : null}
      {fileView ? (
        <FileViewer url={fileView.url} name={fileView.name} onClose={() => setFileView(null)} />
      ) : null}
    </div>
  );
}

// Memoized so a parent re-render with an unchanged `markdown`/`kuerzel` skips the
// reparse entirely — the other half of the flicker fix (the first line of defence
// against CommentItem's hover-state re-renders remounting embedded media).
export const MarkdownContent = memo(MarkdownContentInner);

// Flatten a link's children to plain text (markdown link labels are usually a
// single string; this is just defensive) to sniff a video filename.
function childText(children: ReactNode): string {
  if (typeof children === "string") return children;
  if (Array.isArray(children)) return children.map(childText).join("");
  return "";
}

function Heading({ level, children }: { level: 1 | 2 | 3; children: ReactNode }) {
  const size = level === 1 ? 17 : level === 2 ? 15 : 14;
  const Tag = (level === 1 ? "h3" : level === 2 ? "h4" : "h5") as "h3" | "h4" | "h5";
  return (
    <Tag style={{ margin: "8px 0 4px", fontSize: size, fontWeight: 600, color: "var(--ink-1)" }}>
      {children}
    </Tag>
  );
}
